/**
 * Offline Indicator Component
 * Shows a subtle banner when the user is offline or has pending syncs
 * Automatically hides when connection is restored and all syncs complete
 */

import { useState, useEffect, memo } from 'react';
import { WifiOff, Wifi, RefreshCw, CloudOff, Check } from '@/lib/icons';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

export const OfflineIndicator = memo(function OfflineIndicator() {
  const {
    isOnline,
    wasOffline,
    pendingWriteCount,
    isSyncing,
    triggerSync,
  } = useNetworkStatus();

  const [showReconnected, setShowReconnected] = useState(false);
  const [showSyncComplete, setShowSyncComplete] = useState(false);
  const [previousPendingCount, setPreviousPendingCount] = useState(0);

  // Track when we come back online to show reconnected message
  useEffect(() => {
    if (isOnline && wasOffline && !showReconnected) {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, showReconnected]);

  // Track when pending count drops to 0 to show sync complete message
  useEffect(() => {
    if (previousPendingCount > 0 && pendingWriteCount === 0 && !isSyncing) {
      setShowSyncComplete(true);
      const timer = setTimeout(() => setShowSyncComplete(false), 2000);
      return () => clearTimeout(timer);
    }
    setPreviousPendingCount(pendingWriteCount);
  }, [pendingWriteCount, isSyncing, previousPendingCount]);

  // Determine what to show
  const shouldShow = !isOnline || isSyncing || pendingWriteCount > 0 ||
                     showReconnected || showSyncComplete;

  if (!shouldShow) return null;

  // Render different states
  const renderContent = () => {
    // Offline state - highest priority
    if (!isOnline) {
      return (
        <div className="bg-gray-800/95 text-white flex items-center gap-3">
          <WifiOff className="w-4 h-4 flex-shrink-0" />
          <span>You're offline</span>
          {pendingWriteCount > 0 && (
            <span className="text-white/70 text-xs">
              {pendingWriteCount} {pendingWriteCount === 1 ? 'action' : 'actions'} queued
            </span>
          )}
        </div>
      );
    }

    // Syncing state
    if (isSyncing) {
      return (
        <div className="bg-blue-600/95 text-white flex items-center gap-3">
          <RefreshCw className="w-4 h-4 flex-shrink-0 animate-spin" />
          <span>Syncing...</span>
          {pendingWriteCount > 0 && (
            <span className="text-white/80 text-xs">
              {pendingWriteCount} remaining
            </span>
          )}
        </div>
      );
    }

    // Pending writes (online but not syncing yet)
    if (pendingWriteCount > 0) {
      return (
        <button
          onClick={() => triggerSync()}
          className="bg-amber-600/95 text-white flex items-center gap-3 hover:bg-amber-500/95 transition-colors"
        >
          <CloudOff className="w-4 h-4 flex-shrink-0" />
          <span>{pendingWriteCount} pending {pendingWriteCount === 1 ? 'action' : 'actions'}</span>
          <span className="text-white/80 text-xs">Tap to sync</span>
        </button>
      );
    }

    // Sync complete message
    if (showSyncComplete) {
      return (
        <div className="bg-primary-500/95 text-white flex items-center gap-3">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span>All changes synced</span>
        </div>
      );
    }

    // Just came back online
    if (showReconnected) {
      return (
        <div className="bg-primary-500/95 text-white flex items-center gap-3">
          <Wifi className="w-4 h-4 flex-shrink-0" />
          <span>Back online</span>
        </div>
      );
    }

    return null;
  };

  const content = renderContent();
  if (!content) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] flex justify-center safe-top
                 motion-safe:animate-slide-in-top"
      role="status"
      aria-live="polite"
    >
      <div className="mx-4 mt-2 px-4 py-2.5 rounded-2xl shadow-lg backdrop-blur-xl text-sm font-medium
                     motion-safe:transition-all motion-safe:duration-300">
        {content}
      </div>
    </div>
  );
});
